import React, { useEffect, useState, useRef } from 'react';
import { formatPrice, getOrderStatusColor } from '../utils/helpers';
import { BiChevronDown, BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { Link } from 'react-router-dom';
import { useUserContext } from '../context/user_context';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  SimpleGrid,
  VStack,
  Text,
  Spinner,
  HStack,
  useToast,
  Box,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { useOrderContext } from '../context/order_context';

const ORDERS_PER_PAGE = 20;

function OrdersTable({ orders }) {
  const toast = useToast();
  const { currentUser } = useUserContext();
  const { fetchOrders, deleteOrder } = useOrderContext();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingDelete, setPendingDelete] = useState(null); // { id, name } | null
  const cancelRef = useRef();

  // jump back to page 1 whenever the order list changes (refresh, delete, filter)
  useEffect(() => {
    setCurrentPage(1);
  }, [orders]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const { id } = pendingDelete;
    setPendingDelete(null);
    setLoading(true);
    const response = await deleteOrder(id);
    setLoading(false);
    if (response.success) {
      toast({
        position: 'top',
        description: response.message,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      return await fetchOrders();
    } else {
      return toast({
        position: 'top',
        description: response.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Sort orders by date (newest first)
  const sortedOrders = [...orders].sort((a, b) => {
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const totalPages = Math.ceil(sortedOrders.length / ORDERS_PER_PAGE);
  const startIndex = (currentPage - 1) * ORDERS_PER_PAGE;
  const currentOrders = sortedOrders.slice(startIndex, startIndex + ORDERS_PER_PAGE);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
    <SimpleGrid bg='white' p={5} shadow='lg' borderRadius='lg' overflowX='auto'>
      {loading ? (
        <HStack my={8} alignItems='center' justifyContent='center'>
          <Spinner size='lg' color='brown.500' />
        </HStack>
      ) : (
        <Table variant='simple'>
          <Thead>
            <Tr>
              <Th>Customer</Th>
              <Th>Items</Th>
              <Th>Date</Th>
              <Th>Status</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {currentOrders.map((order) => {
              const {
                user: { name },
                orderItems,
                orderStatus,
                createdAt,
                _id: id,
                itemsPrice,
                shippingPrice,
                totalPrice,
              } = order;
              return (
                <Tr key={id}>
                  <Td>{name}</Td>
                  <Td>
                    <VStack alignItems='flex-start' spacing={5}>
                      {orderItems.map((item, index) => {
                        const { image, name, price } = item;
                        return (
                          <HStack key={index}>
                            <Image
                              src={image}
                              boxSize='50px'
                              objectFit='cover'
                              borderRadius='lg'
                              flexShrink={0}
                            />
                            <VStack alignItems='flex-start' spacing={0}>
                              <Text as='b' fontSize='sm' noOfLines={2}>{name.substring(0, 18)}...</Text>
                              <Text fontSize='xs' color='green.500' whiteSpace='nowrap'>
                                {formatPrice(price)} x{item.quantity || 1} = <Text as='span' color='blue.500' fontWeight='bold'>{formatPrice(price * (item.quantity || 1))}</Text>
                              </Text>
                            </VStack>
                          </HStack>
                        );
                      })}
                      {/* Shipping and Total Breakdown */}
                      <Box borderTop='1px solid' borderColor='gray.200' pt={2} w='full'>
                        <HStack justifyContent='space-between' w='full' fontSize='xs'>
                          <Text color='gray.600'>Subtotal:</Text>
                          <Text fontWeight='semibold'>{formatPrice(itemsPrice)}</Text>
                        </HStack>
                        <HStack justifyContent='space-between' w='full' fontSize='xs'>
                          <Text color='gray.600'>Shipping:</Text>
                          <Text fontWeight='semibold' color={shippingPrice === 0 ? 'green.500' : 'gray.800'}>
                            {shippingPrice === 0 ? 'Free' : formatPrice(shippingPrice)}
                          </Text>
                        </HStack>
                        <HStack justifyContent='space-between' w='full' pt={1} borderTop='1px dashed' borderColor='gray.100'>
                          <Text fontWeight='bold' fontSize='sm'>Total:</Text>
                          <Text fontWeight='bold' fontSize='sm' color='brown.500'>{formatPrice(totalPrice)}</Text>
                        </HStack>
                      </Box>
                    </VStack>
                  </Td>
                  <Td>
                    <Text fontSize='sm' color='gray.600'>
                      {formatDate(createdAt)}
                    </Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={getOrderStatusColor(orderStatus)}>
                      {orderStatus}
                    </Badge>
                  </Td>
                  <Td>
                    <Menu>
                      <MenuButton as={Button} rightIcon={<BiChevronDown />}>
                        Actions
                      </MenuButton>
                      <MenuList>
                        <Link to={`/orders/${id}`}>
                          <MenuItem>View</MenuItem>
                        </Link>
                        {currentUser.privilege !== 'low' && (
                          <MenuItem
                            color='red.500'
                            onClick={() => setPendingDelete({ id, name })}
                          >
                            Delete
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}
      {!loading && totalPages > 1 && (
        <HStack justifyContent='center' spacing={2} mt={5}>
          <Button
            size='sm'
            onClick={() => goToPage(currentPage - 1)}
            isDisabled={currentPage === 1}
            aria-label='Previous page'
          >
            <BiChevronLeft size={18} />
          </Button>
          <Text fontSize='sm' color='gray.600'>
            Page {currentPage} of {totalPages}
          </Text>
          <Button
            size='sm'
            onClick={() => goToPage(currentPage + 1)}
            isDisabled={currentPage === totalPages}
            aria-label='Next page'
          >
            <BiChevronRight size={18} />
          </Button>
        </HStack>
      )}
    </SimpleGrid>

    <AlertDialog
      isOpen={pendingDelete !== null}
      leastDestructiveRef={cancelRef}
      onClose={() => setPendingDelete(null)}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize='lg' fontWeight='bold'>
            Delete Order
          </AlertDialogHeader>

          <AlertDialogBody>
            Are you sure you want to delete
            {pendingDelete?.name ? ` ${pendingDelete.name}'s order` : ' this order'}?
            This action cannot be undone.
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button colorScheme='red' onClick={confirmDelete} ml={3}>
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
    </>
  );
}

export default OrdersTable;
